import { useState } from 'react'
import { Box } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import BannerAlert from '@hapana/shared/web/components/shared/banner-alert/banner-alert.component'
import { SEVERITY } from '@hapana/shared/web/components/shared/banner-alert/banner-alert.constants'
import Button from '@hapana/shared/web/components/shared/button/button.component'
import { BUTTON_VARIANTS } from '@hapana/shared/web/components/shared/button/button.constants'
import TextField from '@hapana/shared/web/components/shared/text-field/text-field.component'
import { TEXT_FIELD_TYPE } from '@hapana/shared/web/components/shared/text-field/text-field.constants'
import {
  APPROVAL_PIN_MAX_LENGTH,
  APPROVAL_PIN_PATTERN,
} from '@hapana/shared/schemas/policy-compliance/policy-evidence-schemas'

import { useVerifyMyApprovalPin } from '_hooks/policy-compliance.hooks'
import { confirmStaffPassword } from '_services/pending-items-desk-unlock.service'
import { useTranslatedResolver } from '_hooks/translated-zod-resolver.hooks'

import {
  DESK_PIN_COPY_VALUES,
  DESK_PIN_FAILURE_COPY,
  resolveDeskPinFailure,
  type DeskPinFailure,
} from '../desk-unlock.utils'
import { PENDING_ITEMS_IDS, buildPendingItemsKey } from '../pending-items.constants'
import { styles } from '../pending-items.styles'

const pinSchema = z.object({ pin: z.string().regex(APPROVAL_PIN_PATTERN, 'pinRequired') })
type PinValues = z.infer<typeof pinSchema>

const passwordSchema = z.object({ staffReentry: z.string().min(1, 'reentryRequired') })
type PasswordValues = z.infer<typeof passwordSchema>

type DeskPinUnlockFormProps = {
  onUnlocked: () => void
}

export const DeskPinUnlockForm = ({ onUnlocked }: DeskPinUnlockFormProps) => {
  const { t } = useTranslation('core')
  const [failure, setFailure] = useState<DeskPinFailure | null>(null)
  const resolver = useTranslatedResolver<PinValues>(pinSchema, buildPendingItemsKey('desk'))
  const formMethods = useForm<PinValues>({ defaultValues: { pin: '' }, resolver })
  const { mutateAsync, isLoading } = useVerifyMyApprovalPin()

  const handleValid = async (values: PinValues) => {
    setFailure(null)
    try {
      await mutateAsync({ pin: values.pin, siteId: null })
      onUnlocked()
    } catch (error) {
      formMethods.reset({ pin: '' })
      setFailure(resolveDeskPinFailure(error))
    }
  }

  return (
    <FormProvider {...formMethods}>
      <Box
        component="form"
        noValidate
        sx={styles.stack}
        onSubmit={formMethods.handleSubmit(handleValid)}
      >
        {failure ? (
          <Box role="alert" data-testid={PENDING_ITEMS_IDS.deskPinError}>
            <BannerAlert
              severity={SEVERITY.ERROR}
              message={t(
                buildPendingItemsKey(DESK_PIN_FAILURE_COPY[failure]),
                DESK_PIN_COPY_VALUES
              )}
            />
          </Box>
        ) : null}
        <TextField
          name="pin"
          textFieldType={TEXT_FIELD_TYPE.PASSWORD}
          topLabel={t(buildPendingItemsKey('desk.pinLabel'))}
          helperText={t(buildPendingItemsKey('desk.pinHelp'), DESK_PIN_COPY_VALUES)}
          fullWidth
          inputProps={{
            inputMode: 'numeric',
            autoComplete: 'off',
            maxLength: APPROVAL_PIN_MAX_LENGTH,
            'aria-label': t(buildPendingItemsKey('desk.pinLabel')),
          }}
          data-testid={PENDING_ITEMS_IDS.deskPin}
        />
        <Button
          variant={BUTTON_VARIANTS.CONTAINED}
          type="submit"
          label={t(buildPendingItemsKey('desk.unlock'))}
          isLoading={isLoading}
          disabled={isLoading}
          data-testid={PENDING_ITEMS_IDS.deskUnlock}
        />
      </Box>
    </FormProvider>
  )
}

type DeskPasswordUnlockFormProps = {
  onUnlocked: () => void
}

export const DeskPasswordUnlockForm = ({ onUnlocked }: DeskPasswordUnlockFormProps) => {
  const { t } = useTranslation('core')
  const [hasFailed, setHasFailed] = useState(false)
  const resolver = useTranslatedResolver<PasswordValues>(
    passwordSchema,
    buildPendingItemsKey('desk')
  )
  const formMethods = useForm<PasswordValues>({ defaultValues: { staffReentry: '' }, resolver })

  const handleValid = async (values: PasswordValues) => {
    setHasFailed(false)
    try {
      await confirmStaffPassword(values.staffReentry)
      onUnlocked()
    } catch {
      formMethods.reset({ staffReentry: '' })
      setHasFailed(true)
    }
  }

  return (
    <FormProvider {...formMethods}>
      <Box
        component="form"
        noValidate
        sx={styles.stack}
        onSubmit={formMethods.handleSubmit(handleValid)}
      >
        {hasFailed ? (
          <Box role="alert">
            <BannerAlert
              severity={SEVERITY.ERROR}
              message={t(buildPendingItemsKey('desk.unlockFailed'))}
            />
          </Box>
        ) : null}
        <TextField
          name="staffReentry"
          textFieldType={TEXT_FIELD_TYPE.PASSWORD}
          topLabel={t(buildPendingItemsKey('desk.reentryLabel'))}
          helperText={t(buildPendingItemsKey('desk.reentryHelp'))}
          fullWidth
          inputProps={{
            autoComplete: 'current-password',
            'aria-label': t(buildPendingItemsKey('desk.reentryLabel')),
          }}
          data-testid={PENDING_ITEMS_IDS.deskReentry}
        />
        <Button
          variant={BUTTON_VARIANTS.CONTAINED}
          type="submit"
          label={t(buildPendingItemsKey('desk.unlock'))}
          isLoading={formMethods.formState.isSubmitting}
          disabled={formMethods.formState.isSubmitting}
          data-testid={PENDING_ITEMS_IDS.deskUnlock}
        />
      </Box>
    </FormProvider>
  )
}
